import React, { useState, useEffect, useRef } from "react";
import { Calendar as CalIcon, Clock, X as XIcon, ChevronLeft, Zap, Sun, Moon, Keyboard, Minus, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * ISO date string (YYYY-MM-DD) for a given Date, in the LOCAL timezone.
 * (Native Date.prototype.toISOString shifts to UTC which flips days.)
 */
export const toISODate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

/** Popover-driven date picker with a "Today" button + a "Clear" option. */
export const DatePicker = ({ value, onChange, testid = "task-edit-date" }) => {
    const selected = value ? new Date(`${value}T00:00:00`) : undefined;
    const displayText = value
        ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
        : "pick a date";

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="w-full mt-1 bg-black/40 border border-meye-border rounded p-2 text-sm text-left flex items-center gap-2 hover:border-meye-cyan/60 focus:outline-none focus:border-meye-cyan/60 transition-colors"
                    data-testid={testid}
                >
                    <CalIcon size={13} className={value ? "text-meye-cyan" : "text-zinc-500"} />
                    <span className={value ? "text-zinc-100" : "text-zinc-500"}>{displayText}</span>
                    {value && (
                        <XIcon
                            size={13}
                            className="ml-auto text-zinc-500 hover:text-red-400"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange("");
                            }}
                            data-testid={`${testid}-clear`}
                        />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                noPortal
                className="w-auto p-0 bg-meye-card border-meye-border"
                align="start"
                data-testid={`${testid}-popover`}
            >
                <div className="flex items-center gap-2 p-2 border-b border-meye-border">
                    <button
                        type="button"
                        onClick={() => onChange(toISODate(new Date()))}
                        className="text-[10px] font-brand uppercase tracking-widest px-2 py-1 rounded border border-meye-cyan/60 text-meye-cyan hover:bg-meye-cyan/10 transition-colors"
                        data-testid={`${testid}-today`}
                    >
                        today
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const d = new Date();
                            d.setDate(d.getDate() + 1);
                            onChange(toISODate(d));
                        }}
                        className="text-[10px] font-brand uppercase tracking-widest px-2 py-1 rounded border border-meye-border text-zinc-400 hover:text-meye-cyan hover:border-meye-cyan/40 transition-colors"
                    >
                        tomorrow
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const d = new Date();
                            d.setDate(d.getDate() + 7);
                            onChange(toISODate(d));
                        }}
                        className="text-[10px] font-brand uppercase tracking-widest px-2 py-1 rounded border border-meye-border text-zinc-400 hover:text-meye-cyan hover:border-meye-cyan/40 transition-colors"
                    >
                        +1 week
                    </button>
                </div>
                <Calendar
                    mode="single"
                    selected={selected}
                    onSelect={(d) => d && onChange(toISODate(d))}
                    initialFocus
                    className="bg-meye-card"
                />
            </PopoverContent>
        </Popover>
    );
};

/**
 * Two-view time picker matching the "Quick picks / Custom time" mockup.
 *
 *   View 1 — QUICK PICKS
 *     • Live preview box with X to clear
 *     • Preset rows: Now · Morning 8:00 · Noon 12:00 · Afternoon 3:00 · Evening 6:00
 *     • "Custom time" row with -/+ that opens View 2
 *     • Bottom action bar: Clear | Set time
 *
 *   View 2 — CUSTOM TIME
 *     • Large H:MM AM display (hour tinted cyan)
 *     • Hour and Minute steppers with -/+
 *     • AM/PM toggle (fuchsia)
 *     • Quick minutes: :00 :15 :30 :45
 *     • Native time input revealed by tapping the display
 *     • Back | Set H:MM AM
 *
 * The `value` prop is a 24h "HH:MM" string (or empty). `onChange` receives the same shape.
 * Nothing commits until the user taps "Set time"/"Set H:MM AM" — closing the popover any
 * other way discards the pending edits.
 */
export const TimePicker = ({ value, onChange, testid = "task-edit-time" }) => {
    const parseVal = (v) => {
        if (!v) return { hour12: 8, minute: 0, ampm: "AM" };
        const [hStr, mStr] = v.split(":");
        const h24 = parseInt(hStr, 10);
        const m = parseInt(mStr, 10);
        const ampm = h24 >= 12 ? "PM" : "AM";
        const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
        return { hour12, minute: m, ampm };
    };
    const toHHMM = (h12, m, ap) => {
        let h24 = h12 % 12;
        if (ap === "PM") h24 += 12;
        return `${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    };
    const fmt = (h12, m, ap) => `${h12}:${String(m).padStart(2, "0")} ${ap}`;

    const parsed = parseVal(value);
    const [open, setOpen] = useState(false);
    // Pending selection — lives locally until the user hits "Set time" / "Set H:MM AM".
    const [pending, setPending] = useState(parsed);
    const [view, setView] = useState("quick");
    const [typing, setTyping] = useState(false);
    const nativeRef = useRef(null);

    // When the popover opens, reset the pending state to whatever is CURRENTLY saved. This
    // avoids stale edits leaking between opens (e.g. user cancels, reopens — should show the
    // last saved value, not the discarded edit).
    useEffect(() => {
        if (open) {
            setPending(parseVal(value));
            setView("quick");
            setTyping(false);
        }
    }, [open]);

    const displayText = value ? fmt(parsed.hour12, parsed.minute, parsed.ampm) : "pick a time";
    const pendingLabel = fmt(pending.hour12, pending.minute, pending.ampm);

    // Preset options — icons + labels + times shown on the right. `Now` is a special dynamic
    // pick that snaps to the current wall clock at commit time (not at render).
    const NOW_KEY = "now";
    const presets = [
        { key: NOW_KEY,     label: "Now",       icon: Zap,  h: null, m: null, ap: null, right: "" },
        { key: "morning",   label: "Morning",   icon: Sun,  h: 8,    m: 0,    ap: "AM", right: "8:00" },
        { key: "noon",      label: "Noon",      icon: Sun,  h: 12,   m: 0,    ap: "PM", right: "12:00" },
        { key: "afternoon", label: "Afternoon", icon: Sun,  h: 3,    m: 0,    ap: "PM", right: "3:00" },
        { key: "evening",   label: "Evening",   icon: Moon, h: 6,    m: 0,    ap: "PM", right: "6:00" },
    ];
    const activePreset = (() => {
        for (const p of presets) {
            if (p.key === NOW_KEY) continue;
            if (p.h === pending.hour12 && p.m === pending.minute && p.ap === pending.ampm) return p.key;
        }
        return null;
    })();

    const applyPreset = (p) => {
        if (p.key === NOW_KEY) {
            const now = new Date();
            const h24 = now.getHours();
            const m = now.getMinutes();
            setPending({
                hour12: h24 % 12 === 0 ? 12 : h24 % 12,
                minute: m,
                ampm: h24 >= 12 ? "PM" : "AM",
            });
            return;
        }
        setPending({ hour12: p.h, minute: p.m, ampm: p.ap });
    };

    // Convert the current pending 12h state to 24h minutes-since-midnight, apply the delta,
    // wrap in 24h, and convert back. Doing math in 24h space is the ONLY reliable way to keep
    // AM/PM correct across the 11→12 boundary — the previous code flipped meridiem on the
    // 12→1 rollover, which produced "12:45 PM + 15 min = 1:00 AM" (regression).
    const shiftMinutes = (prev, deltaMin) => {
        const h24Prev = (prev.hour12 % 12) + (prev.ampm === "PM" ? 12 : 0);
        let total = h24Prev * 60 + prev.minute + deltaMin;
        total = ((total % 1440) + 1440) % 1440;
        const h24 = Math.floor(total / 60);
        const m = total % 60;
        return {
            hour12: h24 % 12 === 0 ? 12 : h24 % 12,
            minute: m,
            ampm: h24 >= 12 ? "PM" : "AM",
        };
    };
    const bumpHour = (delta) => setPending((prev) => shiftMinutes(prev, delta * 60));
    const bumpMinute = (delta) => setPending((prev) => shiftMinutes(prev, delta));

    const commit = () => {
        onChange(toHHMM(pending.hour12, pending.minute, pending.ampm));
        setOpen(false);
    };
    const clearAll = () => {
        onChange("");
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="w-full mt-1 bg-black/40 border border-meye-border rounded p-2 text-sm text-left flex items-center gap-2 hover:border-meye-cyan/60 focus:outline-none focus:border-meye-cyan/60 transition-colors"
                    data-testid={testid}
                >
                    <Clock size={13} className={value ? "text-meye-cyan" : "text-zinc-500"} />
                    <span className={value ? "text-zinc-100" : "text-zinc-500"}>{displayText}</span>
                    {value && (
                        <XIcon
                            size={13}
                            className="ml-auto text-zinc-500 hover:text-red-400"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange("");
                            }}
                            data-testid={`${testid}-clear`}
                        />
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                noPortal
                className="w-[300px] p-0 bg-meye-card border-meye-border rounded-2xl overflow-hidden"
                align="start"
                data-testid={`${testid}-popover`}
            >
                {view === "quick" ? (
                    <div className="p-4 space-y-3" data-testid={`${testid}-quick`}>
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-brand font-bold text-zinc-100">Quick picks</span>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="text-zinc-500 hover:text-zinc-100"
                                data-testid={`${testid}-close`}
                                aria-label="Close"
                            >
                                <XIcon size={14} />
                            </button>
                        </div>

                        {/* Live preview + inline clear */}
                        <div className="flex items-center gap-2 bg-black/40 border border-meye-border rounded-lg px-3 py-2.5">
                            <Clock size={16} className="text-meye-cyan flex-shrink-0" />
                            <span className="flex-1 text-lg font-brand font-bold text-zinc-100">{pendingLabel}</span>
                            <button
                                type="button"
                                onClick={() => setPending(parseVal(""))}
                                className="text-zinc-500 hover:text-red-400"
                                aria-label="Reset preview"
                                data-testid={`${testid}-preview-clear`}
                            >
                                <XIcon size={14} />
                            </button>
                        </div>

                        {/* Preset list */}
                        <div className="space-y-1" data-testid={`${testid}-presets`}>
                            {presets.map((p) => {
                                const Icon = p.icon;
                                const active = p.key === NOW_KEY ? false : activePreset === p.key;
                                return (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => applyPreset(p)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                            active
                                                ? "bg-meye-cyan text-black font-bold"
                                                : p.key === NOW_KEY
                                                    ? "bg-meye-cyan/15 text-meye-cyan hover:bg-meye-cyan/25 border border-meye-cyan/40"
                                                    : "text-zinc-200 hover:bg-meye-card border border-transparent hover:border-meye-border"
                                        }`}
                                        data-testid={`${testid}-preset-${p.key}`}
                                    >
                                        <Icon size={15} strokeWidth={2} />
                                        <span className="flex-1 text-left">{p.label}</span>
                                        {p.right && <span className="font-mono text-xs opacity-80">{p.right}</span>}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Custom time stepper — opens the second view */}
                        <div className="pt-1">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Custom time</p>
                            <div className="grid grid-cols-[auto_1fr_auto] items-stretch rounded-lg border border-meye-border overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => bumpMinute(-15)}
                                    className="px-3 text-zinc-400 hover:bg-meye-card hover:text-meye-cyan"
                                    data-testid={`${testid}-quick-minus`}
                                    aria-label="Subtract 15 minutes"
                                >
                                    <Minus size={14} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setView("custom")}
                                    className="py-2.5 text-center font-brand text-zinc-100 hover:bg-meye-card"
                                    data-testid={`${testid}-open-custom`}
                                >
                                    {pendingLabel}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => bumpMinute(15)}
                                    className="px-3 text-zinc-400 hover:bg-meye-card hover:text-meye-cyan"
                                    data-testid={`${testid}-quick-plus`}
                                    aria-label="Add 15 minutes"
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        </div>

                        {/* Action bar */}
                        <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                                type="button"
                                onClick={clearAll}
                                className="py-2.5 rounded-lg border border-meye-fuchsia/60 text-meye-fuchsia font-brand font-bold hover:bg-meye-fuchsia/10"
                                data-testid={`${testid}-clear-btn`}
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={commit}
                                className="py-2.5 rounded-lg bg-meye-cyan text-black font-brand font-bold hover:bg-cyan-300"
                                data-testid={`${testid}-set`}
                            >
                                Set time
                            </button>
                        </div>
                    </div>
                ) : (
                    /* CUSTOM VIEW */
                    <div className="p-4 space-y-3" data-testid={`${testid}-custom`}>
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => setView("quick")}
                                className="text-meye-cyan flex items-center gap-1 text-sm font-brand font-bold hover:text-cyan-200"
                                data-testid={`${testid}-back`}
                            >
                                <ChevronLeft size={16} strokeWidth={2.5} />
                                <span>Custom time</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="text-zinc-500 hover:text-zinc-100"
                                aria-label="Close"
                            >
                                <XIcon size={14} />
                            </button>
                        </div>

                        {/* Big display — tap to type via native <input type="time"> */}
                        <div className="text-center pt-1">
                            {typing ? (
                                <input
                                    ref={nativeRef}
                                    type="time"
                                    value={toHHMM(pending.hour12, pending.minute, pending.ampm)}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        if (!v) return;
                                        setPending(parseVal(v));
                                    }}
                                    onBlur={() => setTyping(false)}
                                    className="bg-transparent border-b border-meye-cyan text-3xl font-brand font-bold text-zinc-100 text-center focus:outline-none w-40"
                                    data-testid={`${testid}-native`}
                                    autoFocus
                                />
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setTyping(true)}
                                    className="text-3xl font-brand font-bold"
                                    data-testid={`${testid}-tap-to-type`}
                                >
                                    <span className="text-meye-cyan">{pending.hour12}</span>
                                    <span className="text-zinc-500">:</span>
                                    <span className="text-zinc-100">{String(pending.minute).padStart(2, "0")}</span>
                                    <span className="text-zinc-100 ml-1.5">{pending.ampm}</span>
                                </button>
                            )}
                        </div>

                        {/* Hour + Minute steppers */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="text-center">
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Hour</p>
                                <div className="flex items-center justify-center gap-2">
                                    <button type="button" onClick={() => bumpHour(-1)} className="w-8 h-8 rounded border border-meye-border text-zinc-300 hover:text-meye-cyan hover:border-meye-cyan/60" data-testid={`${testid}-h-minus`} aria-label="Hour down">
                                        <Minus size={14} className="mx-auto" />
                                    </button>
                                    <span className="w-8 text-lg font-brand font-bold text-zinc-100">{pending.hour12}</span>
                                    <button type="button" onClick={() => bumpHour(1)} className="w-8 h-8 rounded border border-meye-border text-zinc-300 hover:text-meye-cyan hover:border-meye-cyan/60" data-testid={`${testid}-h-plus`} aria-label="Hour up">
                                        <Plus size={14} className="mx-auto" />
                                    </button>
                                </div>
                            </div>
                            <div className="text-center">
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Minutes</p>
                                <div className="flex items-center justify-center gap-2">
                                    <button type="button" onClick={() => bumpMinute(-1)} className="w-8 h-8 rounded border border-meye-border text-zinc-300 hover:text-meye-cyan hover:border-meye-cyan/60" data-testid={`${testid}-m-minus`} aria-label="Minute down">
                                        <Minus size={14} className="mx-auto" />
                                    </button>
                                    <span className="w-8 text-lg font-brand font-bold text-zinc-100">{String(pending.minute).padStart(2, "0")}</span>
                                    <button type="button" onClick={() => bumpMinute(1)} className="w-8 h-8 rounded border border-meye-border text-zinc-300 hover:text-meye-cyan hover:border-meye-cyan/60" data-testid={`${testid}-m-plus`} aria-label="Minute up">
                                        <Plus size={14} className="mx-auto" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* AM / PM */}
                        <div className="flex items-center justify-center gap-2">
                            {["AM", "PM"].map((ap) => (
                                <button
                                    key={ap}
                                    type="button"
                                    onClick={() => setPending((p) => ({ ...p, ampm: ap }))}
                                    className={`px-5 py-1.5 rounded border text-sm font-brand font-bold transition-colors ${
                                        pending.ampm === ap
                                            ? "border-meye-fuchsia text-meye-fuchsia bg-meye-fuchsia/10"
                                            : "border-meye-border text-zinc-400 hover:text-zinc-100"
                                    }`}
                                    data-testid={`${testid}-ampm-${ap}`}
                                >
                                    {ap}
                                </button>
                            ))}
                        </div>

                        {/* Quick minutes */}
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Quick minutes</p>
                            <div className="grid grid-cols-4 gap-1.5">
                                {[0, 15, 30, 45].map((qm) => (
                                    <button
                                        key={qm}
                                        type="button"
                                        onClick={() => setPending((p) => ({ ...p, minute: qm }))}
                                        className={`py-1.5 rounded border text-sm font-mono font-bold transition-colors ${
                                            pending.minute === qm
                                                ? "border-meye-cyan text-black bg-meye-cyan"
                                                : "border-meye-border text-meye-cyan hover:bg-meye-cyan/10"
                                        }`}
                                        data-testid={`${testid}-qm-${qm}`}
                                    >
                                        :{String(qm).padStart(2, "0")}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Type hint */}
                        <div className="flex items-center justify-center gap-1.5 text-zinc-500 text-[10px]">
                            <Keyboard size={11} />
                            <span>Tap the time to type it</span>
                        </div>

                        {/* Action bar */}
                        <div className="grid grid-cols-2 gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setView("quick")}
                                className="py-2.5 rounded-lg border border-meye-fuchsia/60 text-meye-fuchsia font-brand font-bold hover:bg-meye-fuchsia/10"
                                data-testid={`${testid}-custom-back`}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={commit}
                                className="py-2.5 rounded-lg bg-meye-cyan text-black font-brand font-bold hover:bg-cyan-300"
                                data-testid={`${testid}-custom-set`}
                            >
                                Set {pendingLabel}
                            </button>
                        </div>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
};
